import {flow, types} from 'mobx-state-tree';
import {delay, EElement, ELogicalElement, EStructureItem, EventEmitter} from '@progress-fe/core';
import {IRFNodePort} from '@progress-fe/rf-core';
import {XYPosition} from '@xyflow/react';
import {TechProcessApi} from 'api';
import {IEntityToRefresh} from 'core/interfaces';
import {ProjectBase, RUN_DELAY_MS, TWorkzoneModel} from 'core/models';

import {
  ProjectElements,
  ProjectModels,
  ProjectReactions,
  ProjectLogicalElements,
  ProjectElementsResults,
  ProjectSettings,
  ProjectDataset,
  ProjectTask,
  TPCatalogs
} from './models';

const TechProcessStore = types
  .compose(
    ProjectBase,
    types.model('TechProcessStore', {
      tpCatalogs: types.optional(TPCatalogs, {}),
      projectSettings: types.optional(ProjectSettings, {}),
      projectModels: types.optional(ProjectModels, {}),
      projectElements: types.optional(ProjectElements, {}),
      projectLogicalElements: types.optional(ProjectLogicalElements, {}),
      projectDataset: types.optional(ProjectDataset, {}),
      projectReactions: types.optional(ProjectReactions, {}),
      projectElementsResults: types.optional(ProjectElementsResults, {}),
      projectTask: types.optional(ProjectTask, {})
    })
  )
  .views((self) => ({
    get isFormsUpdating(): boolean {
      return (
        self.projectModels.isFormDataUpdating ||
        self.projectElements.isFormDataUpdating ||
        self.projectTask.isFormDataUpdating ||
        self.projectLogicalElements.isFormDataUpdating ||
        self.projectElementsResults.isFormDataUpdating ||
        self.projectReactions.isFormDataUpdating
      );
    }
  }))
  .actions((self) => ({
    _isUiStateTheSame(uuid: string | null, subUuid: string | null) {
      return !!uuid && self.uiState.entityId === uuid && self.uiState.subEntityId === subUuid;
    }
  }))
  .actions((self) => ({
    _clearAllJsonForms(): void {
      self.projectTask._clearJsonForm();
      self.projectModels._clearJsonForm();
      self.projectElements._clearJsonForm();
      self.projectElementsResults._clearJsonForm();
      self.projectLogicalElements._clearJsonForm();
    },
    // This is called during the first load or after running
    _reloadActiveEntity(): void {
      this._clearAllJsonForms();

      const {entityId, subEntityId} = self.uiState;

      if (self.uiState.entityType === EStructureItem.Element) {
        self.projectElements._reloadJsonForm(entityId, subEntityId).then();
        if (subEntityId) self.projectElements._loadSubWorkZone(entityId).then();
        if (!subEntityId) self.projectElements._clearSubWorkzone();
      } else if (self.uiState.entityType === EStructureItem.Model) {
        self.projectModels._reloadJsonForm(entityId, subEntityId).then();
      } else if (self.uiState.entityType === EStructureItem.LogicalElement) {
        if (entityId) self.projectLogicalElements._reloadJsonForm(entityId).then();
      } else if (self.uiState.entityType === EStructureItem.ElementResult) {
        self.projectElementsResults._reloadJsonForm(entityId).then();
      } else if (self.uiState.entityType === EStructureItem.Task) {
        self.projectTask._reloadJsonForm().then();
        self.projectTask._loadGraphZone().then();
      }
    },
    // This is called by the event emitter
    _selectEntityById(entityId: string, subEntityId?: string): void {
      if (self._isUiStateTheSame(entityId, subEntityId ?? null)) {
        return;
      }

      this._clearAllJsonForms();

      if (self.projectElements.hasElement(entityId)) {
        self._selectUiState(EStructureItem.Element, entityId, subEntityId);
        self.projectElements._reloadJsonForm(entityId, subEntityId).then();
      } else if (self.projectModels.hasModel(entityId)) {
        self._selectUiState(EStructureItem.Model, entityId, subEntityId);
        self.projectModels._reloadJsonForm(entityId, subEntityId).then();
      } else if (self.projectLogicalElements.hasLogicalElement(entityId)) {
        self._selectUiState(EStructureItem.LogicalElement, entityId, subEntityId);
        self.projectLogicalElements._reloadJsonForm(entityId).then();
      } else if (self.projectElementsResults.hasElementResult(entityId)) {
        self._selectUiState(EStructureItem.ElementResult, entityId, subEntityId);
        self.projectElementsResults._reloadJsonForm(entityId).then();
      } else if (self.projectReactions.hasReaction(entityId)) {
        self._selectUiState(EStructureItem.Reaction, entityId, subEntityId);
      }
    },
    // This is called by a structure item or widget zone
    selectEntityByType(entityType: EStructureItem, uuid?: string | null, subUuid?: string | null) {
      if (self._isUiStateTheSame(uuid ?? null, subUuid ?? null)) {
        return;
      }

      this._clearAllJsonForms();
      self._selectUiState(entityType, uuid, subUuid);

      if (entityType === EStructureItem.Element && !!uuid) {
        self.projectElements._reloadJsonForm(uuid, subUuid).then();
        if (subUuid) self.projectElements._loadSubWorkZone(uuid).then();
        if (!subUuid) self.projectElements._clearSubWorkzone();
      } else if (entityType === EStructureItem.Model && !!uuid) {
        self.projectModels._reloadJsonForm(uuid, subUuid).then();
        self.projectElements._clearSubWorkzone();
      } else if (entityType === EStructureItem.Reaction) {
        self.projectElements._clearSubWorkzone();
      } else if (entityType === EStructureItem.Task) {
        self.projectTask._reloadJsonForm().then();
        self.projectTask._loadGraphZone().then();
        self.projectElements._clearSubWorkzone();
      } else if (entityType === EStructureItem.LogicalElement && !!uuid) {
        self.projectLogicalElements._reloadJsonForm(uuid).then();
        self.projectElements._clearSubWorkzone();
      } else if (entityType === EStructureItem.ElementResult) {
        if (uuid) self.projectElementsResults._reloadJsonForm(uuid).then();
        self.projectElements._clearSubWorkzone();
      } else if (entityType === EStructureItem.Settings) {
        self.projectElements._clearSubWorkzone();
      }
    },
    _openSubWorkzoneById(entityId: string): void {
      const subElement = self.projectElements.findFirstSubElement(entityId);
      if (subElement) {
        this.selectEntityByType(EStructureItem.Element, entityId, subElement.uuid);
      }
    },
    _refreshEntities(entityToRefresh: IEntityToRefresh) {
      if (entityToRefresh.dataChanged) {
        self.projectElementsResults._reload().then();
      }
      if (entityToRefresh.modelsChanged) {
        self.projectModels._reload().then();
      }
      if (entityToRefresh.elementsChanged) {
        self.projectElements._reload().then();
      }
      if (entityToRefresh.logicalElementsChanged) {
        self.projectTask._reload().then();
      }
      if (entityToRefresh.treeChanged) {
        self.tpCatalogs.loadTreeCatalog().then();
      }
    },
    backToEntity(entityId?: string | null) {
      this.selectEntityByType(self.uiState.entityType, entityId ?? self.uiState.entityId);
    }
  }))
  .actions((self) => ({
    _subscribe() {
      self._sseSubscribe().then();
      EventEmitter.on('SelectItem', self._selectEntityById);
      EventEmitter.on('DoubleClickItem', self._openSubWorkzoneById);
    },
    _unsubscribe() {
      self._sseUnsubscribe();
      EventEmitter.off('SelectItem', self._selectEntityById);
      EventEmitter.off('DoubleClickItem', self._openSubWorkzoneById);
    },
    uninitialize() {
      this._unsubscribe();
      self.resetModel();
    }
  }))
  .actions((self) => ({
    createElement: flow(function* (type: EElement, position: XYPosition) {
      const result = yield self.projectElements.add(type, position);
      if (result.element) {
        self.tpCatalogs.loadTreeCatalog().then();
        yield self.projectElements._loadElements();
        yield self.projectElements._loadWorkZone();
        self.selectEntityByType(EStructureItem.Element, result.element.uuid);
      }

      if (result.changedEntities) {
        self._refreshEntities(result.changedEntities);
      }

      return result.element;
    }),
    duplicateElement: flow(function* (uuid: string) {
      const newUuid = yield self.projectElements.duplicate(uuid);
      if (newUuid) {
        self.tpCatalogs.loadTreeCatalog().then();
        yield self.projectElements._loadElements();
        yield self.projectElements._loadWorkZone();
        self.selectEntityByType(EStructureItem.Element, newUuid);
      }
    }),
    deleteElements: flow(function* (uuids: Array<string>, skipUpdates?: boolean) {
      const isDone = yield self.projectElements.remove(uuids);
      if (isDone && !skipUpdates) {
        self.tpCatalogs.loadTreeCatalog().then();
        yield self.projectElements._loadElements();
        yield self.projectElements._loadWorkZone();
        self.selectEntityByType(EStructureItem.Settings);
      }
    }),
    async updateElementFormData(
      uuid: string,
      subUuid: string | null,
      schemaId: string,
      data: unknown,
      tabCode?: string
    ) {
      const {_updateElementFormData} = self.projectElements;
      const result = await _updateElementFormData(uuid, subUuid, schemaId, data, tabCode);
      self._clearErrorByUuid(uuid, schemaId);

      if (result.changedEntities) {
        self._refreshEntities(result.changedEntities);
      }
    },
    toggleElementTab: flow(function* (elementUuid: string, tabUniqueCode: string) {
      const result = yield self.projectElements._toggleElementTab(elementUuid, tabUniqueCode);

      if (result?.changedEntities) {
        self._refreshEntities(result.changedEntities);
      }
    })
  }))
  .actions((self) => ({
    createModel: flow(function* (uuid: string) {
      const model = yield self.projectModels.add(uuid);
      if (model) {
        yield self.projectModels._reload();
        self.selectEntityByType(EStructureItem.Model, model.uuid);
      }
    }),
    duplicateModel: flow(function* (uuid: string) {
      const newUuid = yield self.projectModels.duplicate(uuid);
      if (newUuid) {
        yield self.projectModels._reload();
        self.selectEntityByType(EStructureItem.Model, newUuid);
      }
    }),
    deleteModel: flow(function* (uuid: string) {
      const isDone = yield self.projectModels.remove(uuid);
      if (isDone) {
        yield self.projectModels._reload();
        self.selectEntityByType(EStructureItem.Settings);
      }
    }),
    async updateModelFormData(
      uuid: string,
      subUuid: string | null,
      schemaId: string,
      formData: unknown
    ) {
      const {_updateModelFormData} = self.projectModels;
      const result = await _updateModelFormData(uuid, subUuid, schemaId, formData);
      self._clearErrorByUuid(uuid, schemaId);

      if (result?.changedEntities) {
        self._refreshEntities(result.changedEntities);
      }
    }
  }))
  .actions((self) => ({
    createLogicalElement: flow(function* (type: ELogicalElement, position: XYPosition) {
      const logicalElement = yield self.projectLogicalElements.add(type, position);
      if (logicalElement) {
        self.tpCatalogs.loadTreeCatalog().then();
        yield self.projectLogicalElements._loadLogicalElements();
        yield self.projectElements._loadWorkZone();
        yield self.projectTask._loadGraphZone();
        self.selectEntityByType(EStructureItem.LogicalElement, logicalElement.uuid);
      }
    }),
    duplicateLogicalElement: flow(function* (uuid: string) {
      const newUuid = yield self.projectLogicalElements.duplicate(uuid);
      if (newUuid) {
        self.tpCatalogs.loadTreeCatalog().then();
        yield self.projectLogicalElements._loadLogicalElements();
        yield self.projectElements._loadWorkZone();
        yield self.projectTask._loadGraphZone();
        self.selectEntityByType(EStructureItem.LogicalElement, newUuid);
      }
    }),
    deleteLogicalElements: flow(function* (uuids: Array<string>, skipUpdates?: boolean) {
      const isDone = yield self.projectLogicalElements.remove(uuids);
      if (isDone && !skipUpdates) {
        self.tpCatalogs.loadTreeCatalog().then();
        yield self.projectLogicalElements._loadLogicalElements();
        yield self.projectElements._loadWorkZone();
        yield self.projectTask._loadGraphZone();
        self.selectEntityByType(EStructureItem.Settings);
      }
    }),
    async updateLogicalElementFormData(uuid: string, schemaId: string, data: unknown) {
      const {_updateLogicalElementFormData} = self.projectLogicalElements;
      const result = await _updateLogicalElementFormData(uuid, schemaId, data);

      if (result?.workzoneChanged) {
        self.projectElements._loadWorkZone().then();
      }

      if (result?.changedEntities) {
        self._refreshEntities(result.changedEntities);
      }
    }
  }))
  .actions((self) => ({
    async updateTaskFormData(schemaId: string, formData: unknown) {
      const result = await self.projectTask._updateTaskFormData(schemaId, formData);
      self._clearErrorByUuid('', schemaId);

      if (result.changedEntities) {
        self._refreshEntities(result.changedEntities);
      }
    }
  }))
  .actions((self) => ({
    async updateElementResultFormData(uuid: string, schemaId: string, data: unknown) {
      await self.projectElementsResults._updateElementResultFormData(uuid, schemaId, data);
    }
  }))
  .actions((self) => ({
    connectElements: flow(function* (from: IRFNodePort, to: IRFNodePort, refresh: boolean) {
      const isDone = yield self.projectElements.connect(from, to);
      if (isDone && refresh) {
        yield self.projectElements._loadWorkZone();
        const selectedUuid = self.uiState.entityId === from.uuid ? to.uuid : from.uuid;
        self.selectEntityByType(EStructureItem.Element, selectedUuid);
      }
    }),
    disconnectElements: flow(function* (from: IRFNodePort, to: IRFNodePort) {
      const isDone = yield self.projectElements.disconnect(from, to);
      if (isDone) {
        yield self.projectElements._loadWorkZone();
        const selectedUuid = self.uiState.entityId === from.uuid ? to.uuid : from.uuid;
        self.selectEntityByType(EStructureItem.Element, selectedUuid);
      }
    })
  }))
  .actions((self) => ({
    addComponentsToFavorite(componentsIds: string[]) {
      self.projectSettings.addToFavorites(self.projectUuid, componentsIds);
    },
    removeComponentsFromFavorite(componentsIds: string[]) {
      self.projectSettings.removeFromFavorites(self.projectUuid, componentsIds);
    }
  }))
  .actions((self) => ({
    _executeRun: flow(function* () {
      if (!self.runRequest.isPending) {
        yield self.runRequest.send(
          TechProcessApi.techProcessRunTechprocessCalculationTask.bind(TechProcessApi),
          {
            projectUuid: self.projectUuid,
            checkpointUuid: self.checkpointUuid
          }
        );
      }
    }),
    runFinished: flow(function* () {
      console.info('[Store]: Running finished.');
      self._reloadActiveEntity();
      yield self.projectElementsResults._reload();
      yield self._loadJournal();
    })
  }))
  .actions((self) => ({
    batchDeleteElements: flow(function* (items: Array<{uuid: string; isElement: boolean}>) {
      const elements = items.filter((i) => i.isElement);
      const logicalElements = items.filter((i) => !i.isElement);

      if (elements.length > 0) {
        yield self.deleteElements(
          elements.map((i) => i.uuid),
          true
        );
        yield self.projectElements._loadElements();
      }
      if (logicalElements.length > 0) {
        yield self.deleteLogicalElements(
          logicalElements.map((i) => i.uuid),
          true
        );
        yield self.projectLogicalElements._loadLogicalElements();
      }
      self.tpCatalogs.loadTreeCatalog().then();
      yield self.projectElements._loadWorkZone();
      yield self.projectTask._loadGraphZone();

      self.selectEntityByType(EStructureItem.Settings);
    })
  }))
  .actions((self) => {
    const actions = {
      _tryRun: flow(function* () {
        self._runAttempt++;
        console.info(`[Store]: Calculation techprocess attempt ${self._runAttempt}.`);
        if (!self.isFormsUpdating) {
          if (self._runWaiter) {
            clearInterval(self._runWaiter);
            self._runAttempt = 0;
          }
          yield self._executeRun();
        }
      }),
      run: flow(function* () {
        self.isRunning = true;
        yield delay(RUN_DELAY_MS);

        self._runWaiter = setInterval(() => {
          actions._tryRun().then();
        }, RUN_DELAY_MS);
      })
    };
    return actions;
  })
  .actions((self) => ({
    selectDatasetRowKey: flow(function* (rowKey: number) {
      yield self.projectDataset._selectRowKey(rowKey);
      yield Promise.allSettled([self.projectDataset.loadDataset(), self._reloadActiveEntity()]);
    })
  }))
  .actions((self) => ({
    _reloadProjectData: flow(function* (projectUuid: string, checkpointUuid: string) {
      yield Promise.allSettled([
        self.projectSettings.init(projectUuid),
        self.projectModels.init(projectUuid, checkpointUuid),
        self.projectElements.init(projectUuid, checkpointUuid),
        self.projectTask.init(projectUuid, checkpointUuid),
        self.projectElementsResults.init(projectUuid, checkpointUuid),
        self.projectLogicalElements.init(projectUuid, checkpointUuid),
        self.projectDataset.init(projectUuid, checkpointUuid),
        self.tpCatalogs.init(projectUuid, checkpointUuid),
        self._loadJournal()
      ]);
    })
  }))
  .actions((self) => ({
    initProject: flow(function* (projectId: string, checkpointId: string) {
      self.isLoading = true;
      yield self._baseInit(projectId, checkpointId);

      if (self.projectInfo && self.checkpointUuid) {
        yield self._reloadProjectData(self.projectInfo.uuid, self.checkpointUuid);
        self._reloadActiveEntity();
        self._subscribe();
      }

      self.isLoading = false;
    })
  }))
  .views((self) => ({
    get isWorkzoneLoading(): boolean {
      return self.projectElements.isWorkzoneLoading;
    },
    get isSubWorkzone(): boolean {
      return !!self.projectElements.subWorkzone;
    },
    get workzone(): TWorkzoneModel {
      return self.projectElements.subWorkzone || self.projectElements.workzone;
    },
    get nodeList() {
      return this.workzone.nodeList;
    },
    get edgeList() {
      return this.workzone.edgeList;
    },
    get workzoneKey() {
      return `${this.workzone.lastUpdate.toISOString()}_${this.workzone.parentUuid}`;
    }
  }));

export {TechProcessStore};
